import { NumericFormat, NumericFormatProps } from "@sunbound/react-number-format";

interface PercentInputProps extends Omit<NumericFormatProps, `onValueChange` | `customInput`> {
    onValueChange: (value: string) => void;
    as?: NumericFormatProps[`customInput`];
    error?: string | string[];
}

/**
 * PercentInput component for entering percentage values with automatic formatting.
 *
 * Features:
 * - Percent sign suffix (%)
 * - Cursor positioned before the % sign when typing
 * - Two decimal places
 * - No negative values allowed
 * - Range: 0-100
 * - Automatic formatting as you type
 * - Typing "%" moves cursor past the suffix
 * - Can be rendered with custom component via 'as' prop (e.g., as={TextField})
 *
 * @example
 * ```tsx
 * // Basic usage
 * <PercentInput
 *   value={percentage}
 *   onValueChange={(value) => setPercentage(value)}
 *   placeholder="Enter percentage"
 *   className="my-input-class"
 * />
 *
 * // With TextField component
 * <PercentInput
 *   as={TextField}
 *   value={percentage}
 *   onValueChange={(value) => setPercentage(value)}
 *   placeholder="Enter percentage"
 *   error={errors.percentage}
 * />
 * ```
 */
export const PercentInput: React.FC<PercentInputProps> = ({
    onValueChange,
    as,
    ...props
}) => (
    <NumericFormat
        type="text"
        suffix="%"
        decimalSeparator="."
        allowNegative={false}
        decimalScale={2}
        customInput={as}
        isAllowed={(values) => {
            const { floatValue } = values;
            // Allow undefined (empty), or values between 0 and 100
            return floatValue === undefined || (floatValue >= 0 && floatValue <= 100);
        }}
        formatPartialValue={(inputValue): string | void => {
            if (inputValue.trim().endsWith(`%`)) return `%`;
        }}
        onValueChange={(values) => {
            onValueChange(values.value);
        }}
        {...props}
    />
);
